/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import React, { ReactNode, useState } from "react";
import Paper from "@mui/material/Paper";
import partnerCheckinStyle from "./partnerCheckinStyle";
import * as loginActions from "@/src/store/login/actions";
import { useLocation, useNavigate } from "react-router";
import { getLpByLocation } from "@/src/services/landingPagesList";
import { getProductData } from "@/src/services/productService";
import Typography from "@mui/material/Typography";
import { FormProvider } from "@/src/provider/Form/FormProvider";
import PartnerCheckinForm from "./PartnerCheckinForm";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createResource, queryKeys, updateResource } from "@/src/services/reactQuery/reactQueryService";
import { Box } from "@mui/material";
import type { PartnerIri } from "@/src/constants/partnerIris";
import { getPartnerNameByIri } from "@/src/constants/partnerIris";
import _set from "lodash/set";
import _merge from "lodash/merge";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";

type PartnerCheckinProps = {
  idPrefix: string;
  color?: "default" | "grey";
  checkinHeading?: string;
  target?: string;
  partner: PartnerIri;
  additionalFormFields?: Array<{
    key: string;
    initialValue: string | Array<string>;
    fieldToRender: ReactNode;
  }>;
  preSubmitCallback?: (values: Record<string, any>) => Promise<boolean>;
  externalError?: string;
  productName: AbstractCase["name"];
};

const PartnerCheckin = ({
  color = "default",
  target = "/servicewelt",
  idPrefix,
  checkinHeading = "Fall schildern und professionelle Hilfe erhalten",
  partner,
  additionalFormFields,
  preSubmitCallback,
  externalError,
  productName,
}: PartnerCheckinProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const createMutation = useMutation({
    mutationFn: createResource,
    onSuccess: (data, variables) => queryClient.setQueryData(queryKeys.item(variables.uri, data.id), data),
  });
  const updateMutation = useMutation({
    mutationFn: updateResource,
    onSuccess: (data, variables) => queryClient.setQueryData(queryKeys.item(variables.uri, data.id), data),
  });

  const [isLoading, setIsLoading] = useState(false);
  const [emailErrorText, setEmailErrorText] = useState("");

  const additionalInitialValues = (additionalFormFields || []).reduce((accumulator, { key, initialValue }) => {
    return _set(accumulator, key, initialValue);
  }, {});

  const initialValues = _merge(
    {
      customer: { email: "" },
    },
    additionalInitialValues,
  );

  const handleSubmit = async ({ values }: { values: Record<string, any> }) => {
    setIsLoading(true);

    if (preSubmitCallback) {
      const executionIsValid = await preSubmitCallback(values);
      if (!executionIsValid) {
        setIsLoading(false);
        return;
      }
    }

    const { from } = location.state || { from: { pathname: target } };
    const emailCheck = await loginActions.checkEmail(values.customer.email);
    if (emailCheck.hasError) {
      setIsLoading(false);
      setEmailErrorText(emailCheck.errorText);
      return;
    }

    const landingPage = getLpByLocation(location.pathname) || { keyword: location.pathname };
    const partnerName = getPartnerNameByIri(partner);

    const data = _merge(
      {
        gclid: localStorage.getItem("gclid"),
        prelead: {
          initialProduct: productName,
          landingPage: landingPage.keyword,
          partner: partnerName,
        },
      },
      values.customer || {},
    );

    if (data.person) {
      data.person.gender = data.person.gender || null;
    }
    await loginActions.register(data);
    const product: AbstractCase = await createMutation.mutateAsync({
      uri: getProductData(productName, "apiUri"),
      data: _merge(
        {
          acquisitionPartner: partner,
          affiliateCode: localStorage.getItem("gclid"),
          landingPage: landingPage.keyword,
        },
        values.case || {},
      ),
    });

    if (values.caseInsurance) {
      await updateMutation.mutateAsync({
        uri: "/case_insurances",
        id: product.caseInsurance.id,
        data: values.caseInsurance,
      });
    }

    await queryClient.invalidateQueries({
      queryKey: queryKeys.collection("/customers"),
    });
    navigate(from.pathname);
  };

  return (
    <Paper elevation={4} sx={[partnerCheckinStyle.formPaper, partnerCheckinStyle[color]]}>
      <Box id={idPrefix + "-checkin"} />
      <Typography variant={"h5"} sx={partnerCheckinStyle.heading}>
        {checkinHeading}
      </Typography>
      <FormProvider initialValues={initialValues} submitAction={handleSubmit}>
        <PartnerCheckinForm
          isLoading={isLoading}
          emailErrorText={emailErrorText}
          setEmailErrorText={setEmailErrorText}
          additionalFields={additionalFormFields?.map((fieldConfig) => fieldConfig.fieldToRender)}
          externalError={externalError}
        />
      </FormProvider>
    </Paper>
  );
};

export default PartnerCheckin;
